
import { DateTime } from "luxon";
import { ensureUtcIso } from "@/lib/time";

/**
 * Denormalizes an included object from the API response.
 * Used to reconstruct nested relationships.
 * 
 * @param obj The object to denormalize
 * @param included Array of included objects from JSON:API response
 * @param visited Set of visited type:id keys to detect cycles (internal)
 */
export function denormalizeIncludedObject(
  obj: any,
  included: any[],
  visited: Set<string> = new Set()
): any {
  if (!obj) return null;

  // Cycle detection: each object is uniquely identified by type+id
  const key = `${obj.type}:${obj.id}`;
  if (visited.has(key)) {
    // Return a minimal stub to break the cycle and prevent infinite recursion
    return { type: obj.type, id: obj.id, _circular: true };
  }
  visited.add(key);

  const base: any = { type: obj.type, id: obj.id };
  if (obj.attributes) {
    Object.assign(base, obj.attributes);
  }
  if (obj.relationships) {
    Object.entries(obj.relationships).forEach(([key, rel]: [string, any]) => {
      if (!rel || !rel.data) {
        base[key] = Array.isArray(rel?.data) ? [] : null;
        return;
      }
      if (Array.isArray(rel.data)) {
        // Special handling for 'services' relationship: always denormalize to full service objects
        if (key === "services") {
          base[key] = rel.data
            .map((item: any) => {
              // Always look up the full service object in included
              const found = included.find((i: any) => i.type === "service" && i.id === item.id);
              return found ? denormalizeIncludedObject(found, included, visited) : null;
            })
            .filter(Boolean);
        } else {
          base[key] = rel.data
            .map((item: any) => {
              const found = included.find((i: any) => i.type === item.type && i.id === item.id);
              return found ? denormalizeIncludedObject(found, included, visited) : null;
            })
            .filter(Boolean);
        }
      } else if (rel.data && typeof rel.data === "object") {
        const found = included.find((i: any) => i.type === rel.data.type && i.id === rel.data.id);
        base[key] = found ? denormalizeIncludedObject(found, included, visited) : null;
      } else {
        base[key] = rel.data;
      }
    });
  }
  return base;
}

/**
 * Normalizes an appointment record from the API response.
 * Used to flatten and standardize appointment data for internal use.
 */
export function normalizeAppointment(rec: any, included: any[], salonTz: string): any {
  /* ---------- 1. base copy of attributes & relationships ---------- */
  const attr = rec.attributes || {};
  const idStr = String(rec.id);
  const deleted = typeof attr.deleted !== "undefined" ? Number(attr.deleted) : 0;
  const appointment: any = { type: rec.type, id: idStr, ...attr };

  if (rec.relationships) {
    Object.entries(rec.relationships).forEach(([key, rel]: [string, any]) => {
      if (!rel || !rel.data) {
        appointment[key] = Array.isArray(rel?.data) ? [] : null;
        return;
      }

      /* denormalise every relationship into a full object               */
      /* (services, staff, client, goods, etc.)                           */
      if (Array.isArray(rel.data)) {
        appointment[key] = rel.data
          .map((item: any) => {
            const found = included.find(
              (i: any) => i.type === item.type && i.id === item.id
            );
            return found ? denormalizeIncludedObject(found, included) : null;
          })
          .filter(Boolean);
      } else if (typeof rel.data === "object") {
        const found = included.find(
          (i: any) => i.type === rel.data.type && i.id === rel.data.id
        );
        appointment[key] = found
          ? denormalizeIncludedObject(found, included)
          : null;
      } else {
        appointment[key] = rel.data;
      }
    });
  }

  /* ---------- 2. enrich services with price info from attendance_service_items ---------- */
  if (
    Array.isArray(appointment.attendance_service_items) &&
    Array.isArray(appointment.services)
  ) {
    appointment.services = appointment.services.map((service: any) => {
      const matching = appointment.attendance_service_items.find(
        (i: any) => String(i.service_id) === String(service.id)
      );
      if (!matching) return service;
      const toNum = (v: any) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const qty = Math.max(1, Number(matching.quantity ?? service.amount ?? 1));
      const defUnit =
        toNum(matching.cost_per_unit) ??
        toNum(service.cost_per_unit) ??
        toNum(matching.cost) ??
        toNum(service.cost) ??
        toNum(matching.first_cost) ??
        toNum(service.first_cost);
      let manual = toNum(matching.manual_cost); // accept "10" as well as 10
      const nearlyEqual = (a: any, b: any, eps = 0.005) => {
        const ax = Number(a), bx = Number(b);
        return isFinite(ax) && isFinite(bx) && Math.abs(ax - bx) < eps;
      };
      // If manual_cost looks like a LINE TOTAL (common bad seed) rather than a per-unit override, drop it.
      if (manual != null && qty > 1) {
        const lineCandidates = [
          toNum(matching.cost_to_pay_total),
          toNum(matching.default_cost_total),
          toNum(matching.cost),
          toNum(service.cost_to_pay),
          toNum(service.cost),
        ].filter((n) => n != null);
        const isLineTotal = lineCandidates.some((L) => nearlyEqual(manual, L));
        if (isLineTotal) {
          manual = null; // treat as "no manual override"
        }
      }
      // Keep manual_cost only if it differs from the default per-unit price
      const normalizedManual =
        manual != null && (defUnit == null || !nearlyEqual(manual, defUnit))
          ? manual
          : null;
      return {
        ...service,
        first_cost: matching.first_cost,
        // ALWAYS overwrite manual_cost, even if null, to kill any stale value from ...service
        manual_cost: normalizedManual ?? null,
        cost_per_unit: matching.cost_per_unit,
        cost: matching.cost,
        cost_to_pay: matching.cost,
        discount_percent: matching.discount_percent,
        // provenance of an auto-applied discount (last-hour fill badge)
        discount_source: matching.discount_source ?? null,
        amount: matching.quantity,
      };
    });
  }

  // Final guard: if manual_cost still looks like a LINE TOTAL with qty>1, null it.
  if (Array.isArray(appointment.services)) {
    const nearlyEqual = (a: any, b: any, eps = 0.005) => {
      const ax = Number(a), bx = Number(b);
      return Number.isFinite(ax) && Number.isFinite(bx) && Math.abs(ax - bx) < eps;
    };
    appointment.services = appointment.services.map((s: any) => {
      const qty = Math.max(1, Number(s.amount ?? 1));
      if (qty <= 1 || !Number.isFinite(Number(s.manual_cost))) return s;
      const candidates = [s.cost_to_pay_total, s.default_cost_total, s.cost_to_pay, s.cost, s.first_cost]
        .map((v) => Number(v)).filter((n) => Number.isFinite(n));
      const looksLikeLine = candidates.some((L) => nearlyEqual(s.manual_cost, L));
      return looksLikeLine ? { ...s, manual_cost: null } : s;
    });
  }

  /* ---------- 3. normalise linked entities (client / staff / master) ---------- */
  if (appointment.client && typeof appointment.client === "object") {
    appointment.client = {
      type: appointment.client.type,
      id: appointment.client.id,
      name: appointment.client.name,
      surname: appointment.client.surname,
      patronymic: appointment.client.patronymic,
      phone: appointment.client.phone,
      additional_phone: appointment.client.additional_phone,
      email: appointment.client.email,
      comment: appointment.client.comment,
      client_tips: appointment.client.client_tips,
      birthday: appointment.client.birthday,
      card_number: appointment.client.card_number,
      gender: appointment.client.gender,
      discount: appointment.client.discount,
      discount_percent: appointment.client.discount_percent,
      is_new: appointment.client.is_new,
      confirm_method: appointment.client.confirm_method,
      success_visits_count: appointment.client.success_visits_count,
      fail_visits_count: appointment.client.fail_visits_count,
    };
    appointment.client_name = appointment.client.name || "";
    appointment.fullname = appointment.client.name || "";
    // Client is "new" if they have is_new flag true
    appointment.isNew = appointment.client.is_new;
  }

  if (appointment.staff && typeof appointment.staff === "object") {
    appointment.staff = {
      type: appointment.staff.type,
      id: appointment.staff.id,
      name: appointment.staff.name,
      tt_markup: appointment.staff.tt_markup,
      specialization: appointment.staff.specialization,
      image: appointment.staff.image,
      position_id: appointment.staff.position_id,
    };
    appointment.staff_name = appointment.staff.name || "";
  }

  if (appointment.master && typeof appointment.master === "object") {
    appointment.master = {
      type: appointment.master.type,
      id: appointment.master.id,
      name: appointment.master.name,
      tt_markup: appointment.master.tt_markup,
      specialization: appointment.master.specialization,
      image: appointment.master.image,
      position_id: appointment.master.position_id,
    };
    appointment.master_name = appointment.master.name || "";
  }

  /* ---------- 4. derive startTime / endTime (salon timezone) ---------- */
  /* salonTz is provided by the caller */
  const startDT = attr.datetime
    ? DateTime.fromISO(ensureUtcIso(String(attr.datetime), salonTz), { zone: "utc" }).setZone(salonTz)
    : DateTime.fromISO(attr.date, { zone: salonTz });

  const durationSec =
    attr.length !== undefined
      ? Number(attr.length)
      : typeof attr.duration === "number"
        ? attr.duration
        : 0;

  const endDT = startDT.plus({ seconds: durationSec });

  const pad = (n: number) => n.toString().padStart(2, "0");

  appointment.startTime = `${pad(startDT.hour)}:${pad(startDT.minute)}`;
  appointment.endTime = `${pad(endDT.hour)}:${pad(endDT.minute)}`;
  appointment.date = startDT.isValid ? startDT.toISODate() : "";

  // Normalized numeric fields for placement and UTC boundaries
  appointment.startMinOfDay = startDT.isValid ? (startDT.hour * 60 + startDT.minute) : undefined;
  const __durMin = durationSec && Number.isFinite(durationSec) ? Math.max(1, Math.round(durationSec / 60)) : undefined;
  appointment.durationMin = __durMin ?? appointment.durationMin;

  if (attr.datetime) {
    appointment.isoStartUtc = ensureUtcIso(String(attr.datetime), salonTz);
    appointment.isoEndUtc = endDT.toUTC().toISO({ suppressMilliseconds: true });
    // tzVersion is attached by the registry after normalization
    appointment._tzVersion = undefined;
  }

  appointment.deleted = (deleted === 1 ? 1 : 0) as 0 | 1;

  /* ---------- 5. done ---------- */
  return appointment;
}
